/* eslint react-hooks/exhaustive-deps: "warn" */
import { Link } from 'libs/router/Link';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './SalesProspectingLeadGenerationTaxonomy.module.scss';

type CodeRow = {
  flag: string;
  country: string;
  scheme: string;
  code: string;
  activity: string;
  assignedBy: string;
};

const ROWS: CodeRow[] = [
  {
    flag: '🇬🇧',
    country: 'United Kingdom',
    scheme: 'SIC 2007',
    code: '62012',
    activity: 'Business and domestic software development',
    assignedBy: 'Companies House',
  },
  {
    flag: '🇩🇪',
    country: 'Germany',
    scheme: 'WZ 2008',
    code: '62.01',
    activity: 'Programmierungstätigkeiten',
    assignedBy: 'Destatis',
  },
  {
    flag: '🇫🇷',
    country: 'France',
    scheme: 'APE / NAF',
    code: '6201Z',
    activity: 'Programmation informatique',
    assignedBy: 'INSEE',
  },
  {
    flag: '🇮🇹',
    country: 'Italy',
    scheme: 'ATECO',
    code: '62.01.00',
    activity: 'Produzione di software',
    assignedBy: 'Registro Imprese',
  },
  {
    flag: '🇪🇸',
    country: 'Spain',
    scheme: 'CNAE-2009',
    code: '6201',
    activity: 'Programación informática',
    assignedBy: 'Registro Mercantil',
  },
  {
    flag: '🇳🇱',
    country: 'Netherlands',
    scheme: 'SBI 2008',
    code: '6201',
    activity: 'Ontwikkelen en uitgeven van software',
    assignedBy: 'KvK',
  },
  {
    flag: '🇵🇱',
    country: 'Poland',
    scheme: 'PKD 2007',
    code: '62.01.Z',
    activity: 'Działalność związana z oprogramowaniem',
    assignedBy: 'GUS / KRS',
  },
  {
    flag: '🇸🇪',
    country: 'Sweden',
    scheme: 'SNI 2007',
    code: '62010',
    activity: 'Dataprogrammering',
    assignedBy: 'SCB / Bolagsverket',
  },
  {
    flag: '🇺🇸',
    country: 'United States',
    scheme: 'NAICS 2022',
    code: '541511',
    activity: 'Custom computer programming services',
    assignedBy: 'State filings',
  },
  {
    flag: '🇦🇺',
    country: 'Australia',
    scheme: 'ANZSIC',
    code: '7000',
    activity: 'Computer system design services',
    assignedBy: 'ASIC',
  },
  {
    flag: '🇯🇵',
    country: 'Japan',
    scheme: 'JSIC',
    code: '3921',
    activity: 'Software development',
    assignedBy: 'NTA',
  },
  {
    flag: '🇪🇺',
    country: 'Cross-border',
    scheme: 'NACE Rev.2',
    code: '62.01',
    activity: 'Harmonised rollup for pan-European segments',
    assignedBy: 'Mapped',
  },
];

export const SalesProspectingLeadGenerationTaxonomy = () => {
  const { t } = useInt();

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.marker}>
          <span className={styles.markerNum}>04</span> / {t('Taxonomy')}
        </div>

        <div className={styles.body}>
          <div className={styles.head}>
            <TitleBlock
              align="left"
              title={t('One activity. Twelve national codes.')}
              description={t(
                'Every registry assigns its own classification, in its own language. We store the code the registrar issued and map NACE and NAICS beside it, so you can filter on the local scheme or on the cross-border rollup and export both.',
              )}
            />
          </div>

          <div className={styles.shell}>
            <div className={styles.shellBar}>
              <span className={styles.shellTitle}>{t('National classification schemes')}</span>
              <span className={styles.shellMeta}>{t('software development, shown as a worked example')}</span>
            </div>

            <div className={styles.scroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('Country')}</th>
                    <th>{t('Local industry')}</th>
                    <th>{t('Code')}</th>
                    <th>{t('Activity as the registrar states it')}</th>
                    <th className={styles.right}>{t('Assigned by')}</th>
                  </tr>
                </thead>

                <tbody>
                  {ROWS.map((row) => (
                    <tr key={row.scheme}>
                      <td>
                        <span className={styles.country}>
                          <span className={styles.flag} aria-hidden="true">
                            {row.flag}
                          </span>
                          <b>{row.country}</b>
                        </span>
                      </td>

                      <td className={styles.scheme}>{row.scheme}</td>

                      <td className={styles.mono}>{row.code}</td>

                      <td className={styles.activity}>{row.activity}</td>

                      <td className={styles.assignedBy}>{row.assignedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.shellFoot}>
              <span>{t('One activity, twelve national codes. Filter on the local scheme or the NACE rollup.')}</span>
              <span>
                {t('Full scheme coverage in the')}{' '}
                <Link className={styles.footLink} route="/data-dictionary">
                  {t('data dictionary')}
                </Link>
                .
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
